<template>
    <div class="bg-white dark:bg-slate-800 rounded-lg shadow-md dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700">
        <div class="overflow-x-auto">
            <div class="min-w-max">
                <!-- Table Header -->
                <div
                    class="flex rounded-t-lg border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 sticky top-0 z-10"
                >
                    <div
                        v-for="column in filteredColumns"
                        :key="column.key"
                        :class="[
                            'px-6 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors',
                            column.width,
                        ]"
                    >
                        <SortableColumnHeader
                            :label="column.label"
                            :sortable="column.sortable"
                            :sort-key="column.key"
                            :is-active="sortConfig.column === column.key"
                            :justify="
                                column.key === 'value_up_to' ? 'end' : 'start'
                            "
                            @sort="handleSort"
                        />
                    </div>
                    <div class="px-6 py-3 w-32 shrink-0"></div>
                </div>

                <!-- Table Body -->
                <div>
                    <!-- Flat View (Individual Benefits) -->
                    <template v-if="props.viewMode === 'flat'">
                        <BenefitsTableRow
                            v-for="(benefit, index) in props.filteredBenefits"
                            :key="`${benefit.benefit.id}-${benefit.connected_account_id}`"
                            :benefit-item="benefit"
                            :columns="filteredColumns"
                            :index="index"
                            @add-usage="handleUsageAdded"
                            @view-details="handleViewDetails"
                        />
                    </template>

                    <!-- Grouped Views (by Card or by Benefit Type) -->
                    <template v-else>
                        <template
                            v-for="(
                                group, groupIndex
                            ) in props.filteredBenefits"
                            :key="group.key"
                        >
                            <BenefitsCardGroup
                                :card-group="group"
                                :columns="filteredColumns"
                                :index="groupIndex"
                                @add-usage="handleUsageAdded"
                                @view-details="handleViewDetails"
                            />
                        </template>
                    </template>
                </div>

                <!-- Empty State -->
                <div
                    v-if="
                        !loading &&
                        (!props.filteredBenefits ||
                            props.filteredBenefits.length === 0)
                    "
                    class="px-6 py-12 text-center"
                >
                    <div class="text-slate-500 dark:text-slate-400 text-lg">No benefits found</div>
                    <div class="text-slate-400 dark:text-slate-500 text-sm mt-2">
                        Try adjusting your filters or connect more credit cards
                    </div>
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="px-6 py-12 text-center">
                    <div
                        class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"
                    ></div>
                    <div class="text-slate-500 dark:text-slate-400 text-sm mt-2">
                        Loading benefits...
                    </div>
                </div>
            </div>
        </div>

        <!-- Benefits Details Sidebar -->
        <BenefitDetailsSidebar v-if="sidebarOpen" />
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import SortableColumnHeader from '@/components/ui/column-header/sortable-column-header.vue';
import BenefitsTableRow from '@/components/card-benefits/benefits-table-row.vue';
import BenefitsCardGroup from '@/components/card-benefits/benefits-card-group.vue';
import BenefitDetailsSidebar from '@/components/card-benefits/benefit-details-sidebar.vue';

// Props to receive data from parent
const props = defineProps({
    filteredBenefits: {
        type: Array,
        required: true,
    },
    viewMode: {
        type: String,
        required: true,
    },
    hasActiveFilters: {
        type: Boolean,
        default: false,
    },
});

// Emit events to parent
const emit = defineEmits(['usage-added', 'view-details', 'clear-filters']);

const store = useCardBenefitsStore();

const { setSorting, openBenefitDetails } = store;

const { filteredColumns, sortConfig, loading, sidebarOpen } =
    storeToRefs(store);

// Handle sort events from SortableColumnHeader
const handleSort = (direction, sortKey) => {
    setSorting(direction, sortKey);
};

// Event handlers for BenefitsTableRow
const handleUsageAdded = async (benefit) => {
    emit('usage-added', benefit);
};

const handleViewDetails = (benefit) => {
    openBenefitDetails(benefit);
};
</script>
